import { parseResponse } from '$lib/client/responseHelpers';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import { StreamingStatsEvents } from '$lib/services/streaming/statistics';
import type { BroadcastConfig, RTCPeerConnectionExtended } from '$lib/services/streaming/types';

import { getCallBroadcastSession } from '@api/streaming/streaming';
import type { LocalTrackObject } from '@api/types';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { StreamingType, StreamStatus } from '@shared/types/room';

import { createPeerConnection } from './common';

export class Broadcaster {
    private peerConnection?: RTCPeerConnectionExtended;

    public async startBroadcast(config: BroadcastConfig) {
        this.peerConnection = createPeerConnection() as RTCPeerConnectionExtended;
        this.peerConnection.streamingType = StreamingType.Broadcast;
        this.peerConnection.connectionId = window.crypto?.getRandomValues(new Uint32Array(1))?.[0] || 1;
        Emitter.emit(Events.STREAMING_STATS_SET_BROADCAST_START_TIMER);
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_WATCH_PEER_CONNECTION, this.peerConnection);
        this.attachBroadcastPeerConnectionEvents(config.onEvent);

        const peer = this.peerConnection;

        const transceivers = config.mediaStream.getTracks().map((track) =>
            peer.addTransceiver(track, {
                sendEncodings: config.reduceBitrate && track.kind === 'video'
                    ? [{
                        maxBitrate: 120_000,
                        scaleResolutionDownBy: 2,
                        maxFramerate: 15,
                    }]
                    : [{ maxFramerate: 25 }],
                direction: 'sendonly',
            })
        );

        const localOffer =
            await this.callWithTryCatch<RTCSessionDescriptionInit>(
                'rtc_create_offer_error',
                peer.createOffer.bind(peer)
            );

        if (!localOffer || !localOffer.sdp) {
            config.onEvent(StreamStatus.Error);
            return;
        }

        // And apply that offer as the local description.
        const bindedSetLocalSdp = peer.setLocalDescription.bind(peer, localOffer) as () => Promise<void>;
        const setLocalDescriptionResp =
            await this.callWithTryCatch('rtc_set_local_description_error', bindedSetLocalSdp);

        if (setLocalDescriptionResp === null) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: CloudflareCall: setLocalDescription error',
            });
            config.onEvent(StreamStatus.Error);
            return;
        }

        const localTracks = transceivers.map(({ mid, sender }) => ({
            location: 'local',
            mid,
            trackName: sender.track?.id,
        })).filter((item)=> item.mid && item.trackName) as LocalTrackObject[];

        const broadcastConfig = await this.getBroadcastConfigWithRetry(
            localOffer.sdp,
            localTracks
        );

        if (!broadcastConfig) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: CloudflareCall: Error to get broadcast config',
            });
            console.error('Error to get broadcast config for call API');
            config.onEvent(StreamStatus.Error);
            return;
        }

        const bindedSetRemoteSdp = peer.setRemoteDescription
            .bind(peer, new RTCSessionDescription(broadcastConfig.sessionDescription)) as (() => Promise<void>);

        const setRemoteSDPResp = await this.callWithTryCatch('rtc_set_remote_description_error', bindedSetRemoteSdp);

        if (setRemoteSDPResp === null) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: CloudflareCall: Error to set SDP',
            });
            config.onEvent(StreamStatus.Error);
            return;
        }
    }

    private async getBroadcastConfig(sdp: string, tracks: LocalTrackObject[]) {
        try {
            const resp = await getCallBroadcastSession(
                sdp,
                tracks
            );
            const data = parseResponse(resp);
            if (!data) {
                throw new Error('Cloudflare Call Session is not generated');
            }
            return data;
        } catch (error) {
            console.error(error);
            this.statsCallback('rtc_broadcast_config_error', { errorText: (error as Error).message });
            return null;
        }
    }

    private async getBroadcastConfigWithRetry(sdp: string, tracks: LocalTrackObject[]) {
        let retryCount = 3;
        while (retryCount) {
            const response = await this.getBroadcastConfig(sdp, tracks);
            if (response) {
                return response;
            }
            console.error('Error to get CloudFlareCall broadcast config: retry in 1 sec.');
            retryCount -= 1;
            await new Promise<void>((res) => setTimeout(() => res(), 1000));
        }
        streamingLoggerStore.log({
            type: LogType.AppBroadcast,
            message: 'StreamingProvider: CloudflareCall: getConfigWithRetry: retry exceed',
        });
        console.error('getConfigWithRetry: retry exceed');
        return null;
    }

    public stopBroadcast() {
        if (this.peerConnection) {
            this.peerConnection?.close();
            Emitter.emit(Events.STREAMING_STATS_WEBRTC_UNWATCH_PEER_CONNECTION, this.peerConnection);
            Emitter.emit(
                Events.STREAMING_STATS_WEBRTC_SEND_EVENT,
                StreamingStatsEvents.stop_broadcast,
                { streamingType: 'broadcast' }
            );
            this.peerConnection = undefined;
        }
    }

    private attachBroadcastPeerConnectionEvents(updateStatus: (event: StreamStatus | null) => void) {
        if (!(this.peerConnection instanceof RTCPeerConnection)) {
            return;
        }

        const callback = () => {
            switch (this.peerConnection?.connectionState) {
                case 'new':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareCall: connectionState new',
                    });
                    updateStatus(StreamStatus.SocketOpened);

                    break;
                case 'connecting':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareCall: connectionState connecting',
                    });
                    updateStatus(StreamStatus.SocketOpened);

                    break;
                case 'connected':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareCall: connectionState connected',
                    });
                    updateStatus(StreamStatus.PeerConnectionEstablished);

                    break;
                case 'failed':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareCall: connectionState failed',
                    });
                    updateStatus(StreamStatus.Error);

                    break;
                default:
                    streamingLoggerStore.log(
                        {
                            type: LogType.AppBroadcast,
                            message: 'StreamingProvider: CloudflareCall: connectionState unknown',
                        },
                        { state: this.peerConnection?.connectionState },
                        { includeExtendedData: true }
                    );
                    updateStatus(null);

                    break;
            }
        };

        this.peerConnection.addEventListener('connectionstatechange', callback);
        this.peerConnection.addEventListener(
            'icecandidateerror',
            (error) => {
                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: 'StreamingProvider: CloudflareStreaming: broadcast icecandidateerror',
                },
                { ...error },
                { includeExtendedData: true });
            }
        );
        this.peerConnection.addEventListener('iceconnectionstatechange', () => {
            streamingLoggerStore.log(
                {
                    type: LogType.AppBroadcast,
                    message: 'StreamingProvider: CloudflareCall: iceconnectionstate changed',
                },
                { state: this.peerConnection?.iceConnectionState },
                { includeExtendedData: true }
            );
        });
    };

    private statsCallback(event: string, data: unknown) {
        const signalingState = this.peerConnection?.signalingState;
        const streamingType = 'broadcast';

        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType, signalingState });
    }
    private async callWithTryCatch<T>(event:string, callback: () => Promise<T>): Promise<T | null> {
        let result: T;
        try {
            result = await callback();
        } catch (e) {
            console.error(e);
            this.statsCallback(event, { errorText: (e as Error).message });
            return null;
        }
        return result;
    };

}